// components/data-sources/connectorCatalog/CatalogTileGroups.tsx
/**
 * Tiles grouped under eyebrow headings, filtered by a search query, with the
 * loading, error and no-match states. Shared by the "Add data source" modal
 * and the onboarding source step; each brings its own tile.
 */

import { Fragment, type ReactNode } from "react";
import { Alert, Button, Center, Group, Loader, SimpleGrid, Stack, Text, TextInput } from "@mantine/core";
import { IconAlertTriangle, IconRefresh, IconSearch } from "@tabler/icons-react";

import { CATALOG_GROUPS, type CatalogGroupKey } from "./catalogGroups";

export type CatalogTileBase = {
  key: string;
  label: string;
  subtitle?: string;
  group: CatalogGroupKey;
};

/** Case-insensitive match on label, subtitle and group label. */
export function filterTiles<T extends CatalogTileBase>(tiles: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return tiles;
  return tiles.filter((t) => {
    const group = CATALOG_GROUPS.find((g) => g.key === t.group)?.label ?? "";
    return [t.label, t.subtitle ?? "", group].some((s) => s.toLowerCase().includes(q));
  });
}

/** Tiles per group, in group order; empty groups are left out. */
export function groupTiles<T extends CatalogTileBase>(
  tiles: T[]
): Array<{ key: CatalogGroupKey; label: string; tiles: T[] }> {
  return CATALOG_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    tiles: tiles.filter((t) => t.group === g.key),
  })).filter((g) => g.tiles.length > 0);
}

export function CatalogSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <TextInput
      aria-label="Search sources"
      placeholder="Search sources"
      leftSection={<IconSearch size={16} />}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

type Props<T extends CatalogTileBase> = {
  tiles: T[];
  query: string;
  /** The managed-connector catalogue is still loading. */
  loading: boolean;
  /** The catalogue failed; tiles that do not depend on it still show. */
  error: string | null;
  onRetry: () => void;
  renderTile: (tile: T) => ReactNode;
  cols?: React.ComponentProps<typeof SimpleGrid>["cols"];
};

export function CatalogTileGroups<T extends CatalogTileBase>({
  tiles,
  query,
  loading,
  error,
  onRetry,
  renderTile,
  cols = { base: 1, xs: 2, sm: 3 },
}: Props<T>) {
  if (loading) {
    return (
      <Center py="xl">
        <Group gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading sources…
          </Text>
        </Group>
      </Center>
    );
  }

  const visible = filterTiles(tiles, query);
  const groups = groupTiles(visible);

  return (
    <Stack gap="lg">
      {error && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
          title="Some sources could not be loaded"
        >
          <Stack gap="xs" align="flex-start">
            <Text size="sm">{error}</Text>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={onRetry}
            >
              Try again
            </Button>
          </Stack>
        </Alert>
      )}

      {query.trim() && groups.length === 0 ? (
        <Stack gap={4} py="lg" align="center">
          <Text fw={600}>No sources match “{query.trim()}”.</Text>
          <Text size="sm" c="dimmed">
            Check the spelling or search for a category, such as “payments”.
          </Text>
        </Stack>
      ) : (
        groups.map((g) => (
          <Stack key={g.key} gap="xs" data-testid={`catalog-group-${g.key}`}>
            <Text
              component="h3"
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              style={{ letterSpacing: "0.06em", margin: 0 }}
            >
              {g.label}
            </Text>
            <SimpleGrid cols={cols} spacing="md">
              {g.tiles.map((t) => (
                <Fragment key={t.key}>{renderTile(t)}</Fragment>
              ))}
            </SimpleGrid>
          </Stack>
        ))
      )}
    </Stack>
  );
}
